import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tabs, message } from "antd";
import { ColumnType, TableProps } from "antd/es/table";
import UserApi from "../../../../api/UserApi";
import StringUtils from "../../../../util/StringUtils";
import { CreateUserWizardWrapper, FormWrapper, OperationWrapper, Title, TitleWrapper, ToolBarWrapper, UserInformationWrapper, UserRolesWrapper } from "./CreateUserWizard.styled";
import PermissionApi from "../../../../api/PermissionApi";

interface CreateUserWizardProps {
    editingUserId?: string,

    onClose: ()=> void,
    onComplete: ()=> void
}

const CreateUserWizard: React.FC<CreateUserWizardProps> = (props)=> {
    // api
    let userApi = new UserApi();
    let permissionApi = new PermissionApi();
    const [messageApi, contextHolder] = message.useMessage();

    // form
    const [form] = Form.useForm();

    // state
    let [user, setUser] = useState<any>({});
    let [userRoles, setUserRoles] = useState([]);
    let [roles, setRoles] = useState([]);
    let [openRoleSelectionModal, setOpenRoleSelectionModal] = useState(false);
    let [submitEnabled, setSubmitEnabled] = useState(false);
    let [loading, setLoading] = useState(false);

    // functions
    let loadUserProfileAndPermissions = async ()=> {
        let user = (await userApi.get(props.editingUserId)).data.data;
        form.setFieldsValue(user);
        setUser(user);

        let userRoles = (await permissionApi.getUserRoles(props.editingUserId)).data.data;
        setUserRoles(userRoles);

        loadTenantRoles();
    }

    let loadTenantRoles = async ()=> {
        // tenant roles
        let roles = (await permissionApi.getRoles()).data.data;
        setRoles(roles);
    }

    // event
    let onNameChange = (e)=> {
        user.name = e.target.value;
        validateForm();
    };

    let onPhoneNumberChange = (e)=> {
        user.phoneNumber = e.target.value;
        validateForm();
    };

    let onEmailChange = (e)=> {
        user.phoneNumber = e.target.value;
        validateForm();
    };

    let onAdminChange = (value)=> {
        user.admin = value;
        validateForm();
    };

    let onPasswordChange = (e)=> {
        user.password = e.target.value;
        validateForm();
    }
    
    let onRemarksChange = (e)=> {
        user.remarks = e.target.value;
        validateForm();
    };

    let onSubmit = async ()=> {
        // save user
        let userId = user.id;
        if (props.editingUserId) {
            await userApi.update(user);
        } else {
            let savedUser = (await userApi.create(user)).data.data;
            userId = savedUser.id;
        }
        
        // set user roles
        let roleIds = userRoles.map(userRole => userRole.id);
        await permissionApi.setUserRoles(userId, roleIds);

        // callbacks
        props.onComplete();
        props.onClose();
    };

    let onCancel = ()=> {
        props.onClose();
    };
    
    let onDeleteRole = (role: any)=> {
        userRoles = userRoles.filter(userRole => userRole.id !== role.id);
        setUserRoles(userRoles);
        validateForm();
    };

    // validate
    let validateForm = ()=> {
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            console.log(err);
        });
    }

    // effect
    useEffect(()=> {
    }, []);
    
    useEffect(()=> {
        if (props.editingUserId) {
            loadUserProfileAndPermissions();
        }
    }, [props.editingUserId])

    // render

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '名称',
            dataIndex: 'role.name',
            key: 'role.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.name}</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'role.remarks',
            key: 'role.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    {<Popconfirm
                            title="删除角色"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认要删除么?</div> }
                            onConfirm={()=> onDeleteRole(record)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>删除</a>
                    </Popconfirm> }
                </Space>
            }
        }
    ];

    let roleSelectionColumns: ColumnType<any>[] = [
        {
            title: '名称',
            dataIndex: 'role.name',
            key: 'role.name',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.name}</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'role.remarks',
            key: 'role.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.remarks)}</span>
            }
        },
    ];

    const tenantRolesRowSelection: TableProps<any>['rowSelection'] = {
        type: 'radio',
        selectedRowKeys: [],
        onSelect: (record, selected, selectedRows) => {
            // add to user roles
            let alreadyExist = false;
            for(let userRole of userRoles) {
                if (userRole.id === record.id) {
                    alreadyExist = true;
                    break;
                }
            }
            if (!alreadyExist) {
                userRoles.push(record);
                setUserRoles([...userRoles]);
            }

            // hide modal
            setOpenRoleSelectionModal(false);
            validateForm();
        },
    };

    
    let tabItems = [
        {
            key: 'user-information',
            label: '基本信息',
            children: <UserInformationWrapper>
                <FormWrapper>
                    <Form form={form} name="validateOnly" layout="vertical">
                        <Form.Item name="name" label="用户名" rules={[{required: true}]} initialValue={user.name}>
                            <Input value={user.name} onChange={onNameChange} placeholder="请输入用户名" />
                        </Form.Item>

                        <Form.Item name="phoneNumber" label="电话号码" rules={[{required: false}]} initialValue={user.phoneNumber}>
                            <Input value={user.phoneNumber} onChange={onPhoneNumberChange} placeholder="请输入手机号" />
                        </Form.Item>

                        <Form.Item name="email" label="电子邮箱" rules={[{required: false}]} initialValue={user.email}>
                            <Input value={user.phoneNumber} onChange={onEmailChange} placeholder="请输入电子邮箱" />
                        </Form.Item>

                        <Form.Item name="admin" label="管理员用户" rules={[{required: false}]} initialValue={user.isAdmin || user.admin}>
                            <Switch checked={user.isAdmin || user.admin} onChange={onAdminChange}/> 
                        </Form.Item>

                        <Form.Item name="password" label="密码" rules={[{required: props.editingUserId ? false: true}]} initialValue={user.password}>
                            <Input.Password value={user.password} onChange={onPasswordChange} placeholder="请输入密码" />
                        </Form.Item>

                        <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={user.remarks}>
                            <Input.TextArea value={user.remarks} style={{height: '100px'}} onChange={onRemarksChange} placeholder="请输入用户备注"/>
                        </Form.Item>
                    </Form>
                </FormWrapper>
            </UserInformationWrapper>
        },
        {
            key: 'user-roles',
            label: '用户角色',
            children: <UserRolesWrapper>
                <ToolBarWrapper>
                    <Button type="primary" onClick={() => setOpenRoleSelectionModal(true)}>新增角色</Button>
                    <Modal open={openRoleSelectionModal} 
                        onCancel={()=> setOpenRoleSelectionModal(false)} 
                        onOk={()=> setOpenRoleSelectionModal(false)}
                        title="选择角色"
                        cancelButtonProps={{style: {display: 'none'}}}
                        closable={false}
                        destroyOnClose={true}
                        width={1000}
                        >
                        
                        <Table
                            rowKey={(record) => record.id}
                            columns={roleSelectionColumns}
                            dataSource={roles}
                            pagination={null}
                            loading={loading}
                            rowSelection={tenantRolesRowSelection}
                            onRow={(record)=> {
                                return {
                                    onClick: (e)=> {
                                        tenantRolesRowSelection.onSelect(record, null, null, null);
                                    }
                                }
                            }}
                        />
                        
                    </Modal>
                </ToolBarWrapper>
                <Table
                    rowKey={(record) => record.id}
                    columns={columns}
                    dataSource={userRoles}
                    pagination={null}
                    loading={loading}
                />
            </UserRolesWrapper>
        },
    ];

    return (
        <CreateUserWizardWrapper>
            <TitleWrapper>
                <Title>{props.editingUserId ? '编辑用户' : '新增用户'}</Title>
            </TitleWrapper>

            <Tabs
                defaultActiveKey={'user-information'}
                items={tabItems}
                style={{flex: '1 1 auto', overflow: 'hidden'}}
            />

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </CreateUserWizardWrapper>
    )
}

export default CreateUserWizard;
